import React, {useState} from 'react';
import Storage from 'src/utils/Storage';
import {Button, Tooltip} from '@material-ui/core';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import {
  PriorAuthConstants,
  TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER
} from '@shared/constants/Mapping';
import {APP, DEFAULT_RED_COLOR} from 'src/style/variable';
import {useTranslation} from 'react-i18next';
import {TicketingSystemTimerStyles} from './TicketingSystemTimerStyles';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {startTicketTimer} from '@shared/services/WorkflowServices';
import {useDispatch, useSelector} from 'react-redux';
import {
  _startTicketTimer,
  _stopTicketTimer
} from '@shared/store/actions/TicketTimerActions';
import StopTicketTimerDialog from './StopTicketTimerDialog/StopTicketTimerDialog';
import HelperUtils from '@shared/utils/HelperUtils';
import TertiaryButton from '@components/system/ButtonField/TertiaryButton';
import {CommunicationService} from 'src/service/CommunicationService';
import TicketRunningTime from './TicketRunningTime';
import {IAllPossibleTicketModules} from '../hooks/useTicketingSystemPermission';

interface ITicketingSystemTimerProps {
  clinicGroupId?: number;
  clinicId?: number;
  patientId?: number;
  ticketId?: number;
  selectedTicketDetails?: any;
  isFromFloatingTicketPanel?: boolean;
  module: IAllPossibleTicketModules;
}
const TicketingSystemTimer = (
  props: ITicketingSystemTimerProps
): JSX.Element => {
  const {
    clinicGroupId,
    clinicId,
    ticketId,
    selectedTicketDetails,
    isFromFloatingTicketPanel = false,
    module
  } = props;
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const classes = TicketingSystemTimerStyles();
  const dispatch = useDispatch();
  const TicketTimerInfo = useSelector(
    (state: any) => state.PriorAuthTicketTimer
  );
  const {isTicketTimerRunning, startTime} = TicketTimerInfo;
  const [showDialog, setShowDialog] = useState({
    showStopTicketTimerDialog: false
  });
  const {showStopTicketTimerDialog} = showDialog;
  // reading patient id, guarantorId, patientDetails from the selected ticket
  const patientId: number = selectedTicketDetails?.data?.patient?.id || null;
  const guarantorId: number =
    selectedTicketDetails?.data?.patient?.guarantor?.id || null;
  const patientDetails: any = selectedTicketDetails?.data?.patient || null;

  // setting the values in localstorage of the ticket for which the timer is running
  const updateSelectedPatientTicketTimerData = (
    res: any,
    currentDate: Date
  ) => {
    const currentTime = currentDate.getTime();
    const ticketPanelData = {
      clinicGroupId: clinicGroupId,
      clinicId: clinicId ?? TicketTimerInfo?.clinicId,
      patientId: patientId ?? TicketTimerInfo?.patientId,
      ticketId: ticketId ?? TicketTimerInfo.ticketId,
      logId: res?.data?.id,
      guarantorId: guarantorId ?? TicketTimerInfo?.guarantorId,
      patientName: HelperUtils.commaSeparatedFullName(
        patientDetails?.first_name,
        patientDetails?.last_name,
        patientDetails?.middle_name
      ),
      patientAccountNumber: patientDetails?.account_number,
      module
    };
    Storage.setItem(
      PriorAuthConstants.STORAGE_PRIOR_AUTH_TIMER_DATA,
      JSON.stringify({
        startTime: currentTime,
        isTicketTimerRunning: true,
        patientId: patientId,
        ticketId: ticketId,
        clinicGroupId: clinicGroupId,
        clinicId: clinicId ?? TicketTimerInfo.clinicId,
        logId: res?.data?.id,
        ticketPanelData,
        module
      })
    );
    // need to dispatch the log_id here....
    dispatch(_startTicketTimer(currentTime, ticketId, patientId, res?.data.id));
  };

  // Initialization method, when clicked on startTimer button this is the entry point
  const initializePriorAuthTimer = async () => {
    try {
      const date = new Date();
      const utcDate = date.toUTCString();
      const payload: any = {
        start_time: utcDate
      };
      if (clinicId && ticketId && clinicGroupId) {
        const res = await startTicketTimer(
          clinicGroupId,
          clinicId,
          ticketId,
          payload,
          // TODO: MAKE THE TIMER GENERIC
          {
            module: TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER[module]
          }
        );
        if (res.data) {
          // now call the function to set the response in local storage
          updateSelectedPatientTicketTimerData(res, date);
          enqueueSnackbar(
            t('TICKETING_SYSTEM.TIMER.START_TIMER'),
            SnackBarConfig('s')
          );
        }
      }
    } catch (error: any) {
      enqueueSnackbar(error.message, SnackBarConfig('e'));
    }
  };

  const handleStartPriorAuthTimer = () => {
    if (!isTicketTimerRunning) {
      initializePriorAuthTimer();
    } else {
      // TODO: need to handle this scenario if timer is running for the one of the ticket
      enqueueSnackbar(
        t('TICKETING_SYSTEM.TIMER.TIMER_IS_RUNNING_ALREADY'),
        SnackBarConfig('e')
      );
    }
  };

  // get the timer status
  const getPriorAuthTimerStatus = () => {
    let runningTimer = false;
    let timerGroupId: number | undefined;
    let timerTicketId: number | undefined;

    const storedPriorAuthTimerData = Storage.getItem(
      PriorAuthConstants.STORAGE_PRIOR_AUTH_TIMER_DATA
    );
    if (storedPriorAuthTimerData) {
      const parsedTimerData = JSON.parse(storedPriorAuthTimerData);
      timerGroupId = parsedTimerData?.clinicGroupId;
      timerTicketId = parsedTimerData?.ticketId;
    }
    if (timerGroupId != clinicGroupId) {
      return runningTimer;
    }
    if (isTicketTimerRunning) {
      if (ticketId && ticketId === timerTicketId) {
        runningTimer = true;
      }
    }
    return runningTimer;
  };

  const handleStopPriorAuthTimer = () => {
    const currentTime = Date.now();
    dispatch(_stopTicketTimer(currentTime));
    setShowDialog(prevState => ({
      ...prevState,
      showStopTicketTimerDialog: true
    }));
  };
  const renderDefaultPriorAuthTimer = () => {
    return (
      <div>
        {getPriorAuthTimerStatus() ? (
          <div
            className={classes.stopTimeWrapperDefault}
            style={{
              padding: '0.5rem'
            }}>
            <div className={classes.runningTimeDiv}>
              <TicketRunningTime startTime={new Date(startTime)} />
            </div>
            <div className={classes.stopTimerButtonWrapper}>
              <Button
                startIcon={
                  <AccessTimeIcon className={classes.accessTimeIcon} />
                }
                className={classes.stopTimerButton}
                variant="outlined"
                onClick={() => {
                  handleStopPriorAuthTimer();
                }}>
                <span className={classes.stopTimerButtonLabel}>
                  {t('CCM.STOP_TIMER.STOP_TIMER')}
                </span>
              </Button>
            </div>
          </div>
        ) : (
          <Tooltip title={t('CCM.START_TIMER.START_TIMER').toString()}>
            <TertiaryButton
              className={classes.ticketStartTimer}
              startIcon={<AccessTimeIcon />}
              onClick={() => handleStartPriorAuthTimer()}>
              {t('CCM.START_TIMER.START_TIMER')}
            </TertiaryButton>
          </Tooltip>
        )}
      </div>
    );
  };

  const renderTicketFloatingPanel = () => {
    return (
      <div className={classes.floatingTimerWrapper}>
        <TicketRunningTime startTime={new Date(startTime)} />
        <Button
          className={classes.stopTimeFloatingTimer}
          variant="outlined"
          style={{
            color: APP.COLOR.DEFAULT.WHITE,
            backgroundColor: DEFAULT_RED_COLOR
          }}
          onClick={() => {
            handleStopPriorAuthTimer();
          }}>
          <span className={classes.stopTimerLabel}>
            {t('CCM.STOP_TIMER.STOP_TIMER')}
          </span>
        </Button>
      </div>
    );
  };

  const refreshActivity = () => {
    // **NOTE: Trigger an event after updated to get the latest details in action history */
    CommunicationService.sendEvent({
      action: 'TICKET_TIMER_PANEL_DATA_UPDATED',
      data: null
    });
  };
  return (
    <>
      {isFromFloatingTicketPanel
        ? renderTicketFloatingPanel()
        : renderDefaultPriorAuthTimer()}
      {showStopTicketTimerDialog && (
        <StopTicketTimerDialog
          open={showStopTicketTimerDialog}
          onClose={(res: any) => {
            setShowDialog(prevState => ({
              ...prevState,
              showStopTicketTimerDialog: false
            }));
            refreshActivity();
          }}
          clinicGroupId={clinicGroupId}
          clinicId={clinicId}
          ticketId={ticketId}
        />
      )}
    </>
  );
};
export default TicketingSystemTimer;
